import { BlockType } from '../../../playground/shared'

// only here for easier testing
export const defaultMesherConfig = {
  version: '',
  worldMaxY: 256,
  worldMinY: 0,
  enableLighting: true,
  skyLight: 15,
  smoothLighting: true,
  shadingTheme: 'high-contrast',
  cardinalLight: 'default',
  outputFormat: 'threeJs' as 'threeJs' | 'webgpu',
  // textureSize: 1024, // for testing
  debugModelVariant: undefined as undefined | number[],
  clipWorldBelowY: undefined as undefined | number,
  disableBlockEntityTextures: false,
  // Phase 6.1 — Greedy meshing. When true:
  //   - mesher emits tile-local UVs (0..1 instead of atlas absolute)
  //   - mesher emits a tileBounds attribute per vertex (4 u16 = atlas tile origin + size)
  //   - renderer applies a custom shader that re-projects:
  //       atlas_uv = tileBounds.xy + fract(vUv) * tileBounds.zw
  //   - greedy merger combines adjacent same-tile same-color faces into larger quads,
  //     UVs tile via fract() so atlas remains correct
  // Stage 1 (current): infra only — passthrough behaves identically to non-greedy path.
  // Stage 2: actual greedy merging in the mesher.
  greedyMesh: false,
}

export type CustomBlockModels = {
  [blockPosKey: string]: string // blockPosKey is "x,y,z" -> model name
}

export type MesherConfig = typeof defaultMesherConfig

export type MesherGeometryOutput = {
  sx: number,
  sy: number,
  sz: number,
  // resulting: float32array
  positions: any,
  normals: any,
  colors: any,
  uvs: any,
  // Phase 6.1 — Greedy meshing path. Present only when greedyMesh=true was set.
  // When present, `uvs` are tile-local 0..N (N=quad size in tiles) and `tileBounds`
  // is a per-vertex Uint16Array (4 components: atlas u, atlas v, tile width, tile height).
  // Renderer reprojects in fragment shader: atlasUv = tileBounds.xy + fract(uv) * tileBounds.zw.
  tileBounds?: any,
  t_positions?: number[],
  t_normals?: number[],
  t_colors?: number[],
  t_uvs?: number[],
  t_tileBounds?: number[],

  indices: Uint32Array | Uint16Array | number[],
  indicesCount: number,
  using32Array: boolean,
  tiles: Record<string, BlockType>,
  heads: Record<string, any>,
  signs: Record<string, any>,
  banners: Record<string, any>,
  // isFull: boolean
  hadErrors: boolean
  blocksCount: number
  customBlockModels?: CustomBlockModels
}

export interface MesherMainEvents {
  geometry: { type: 'geometry'; key: string; geometry: MesherGeometryOutput; workerIndex: number };
  sectionFinished: { type: 'sectionFinished'; key: string; workerIndex: number; processTime?: number };
  blockStateModelInfo: { type: 'blockStateModelInfo'; info: Record<string, BlockStateModelInfo> };
  heightmap: { type: 'heightmap'; key: string; heightmap: Uint8Array };
  /** Reply to `{ type: 'mc-web-ping', t?, workerIndex? }` from the main thread (not batched in worker). */
  mcWebPong: { type: 'mc-web-pong'; workerIndex: number; t?: number; recvAt?: number };
}

export type MesherMainEvent = MesherMainEvents[keyof MesherMainEvents]

export type HighestBlockInfo = { y: number, stateId: number | undefined, biomeId: number | undefined }

export type BlockStateModelInfo = {
  cacheKey: string
  issues: string[]
  modelNames: string[]
  conditions: string[]
}

export const getBlockAssetsCacheKey = (stateId: number, modelNameOverride?: string) => {
  return modelNameOverride ? `${stateId}:${modelNameOverride}` : String(stateId)
}
